/**
 * Hook that fetches the four tRPC queries for the weekly proposal screen
 * and merges them into a unified view-model.
 *
 * Week resolution: uses currentOrderingWeek from @retail/dates (TZ pinned) —
 * the week whose cutoff is the next to come, which is the week the store is
 * ordering FOR, not the week on the clock — with the STORE's own cutoff rule
 * when it has one, so two stores can be ordering for different weeks.
 */
import {useMemo} from 'react'
import {trpc} from '../../trpc'
import type {CutoffRule} from '@retail/dates'
import {currentOrderingWeek, cutoffForWeek, startOfWetacaWeek} from '@retail/dates'
import {useNowAcrossCutoff} from '../../hooks/useNowAcrossCutoff'
import {getErrorCode} from '../../lib/errorCode'
import {mergeProposalViewModel} from './proposalViewModel'
import type {ProposalViewModel, StoreRole} from './proposalViewModel'

type UseWeeklyProposalInput = {
  storeId: number
  role: StoreRole
  /** The store's per-line floor — every row reads at it or above. */
  minQuantityPerProduct: number
  /** The store's own cutoff rule; absent = the general one (Wednesday 13:00 Madrid). */
  cutoff?: CutoffRule
}

/**
 * Given the active store id and role, runs currentMenu + eligibleProducts +
 * proposalForWeek queries and returns a merged ProposalViewModel.
 *
 * Switching stores changes the query keys, which triggers fresh fetches on its
 * own. The caller resolves the store before rendering the page, so storeId is
 * always a real id by the time it gets here.
 */
export const useWeeklyProposal = ({
  storeId,
  role,
  minQuantityPerProduct,
  cutoff,
}: UseWeeklyProposalInput): ProposalViewModel => {
  // Resolve the week being ordered right now — editing happens BEFORE the menu
  // week starts, at the store's own cutoff. currentOrderingWeek is TZ-pinned;
  // safe to call in the browser.
  const {week: isoWeek, year: isoYear} = currentOrderingWeek(new Date(), cutoff)

  const storeInput = {storeId, isoYear, isoWeek}

  const menuQuery = trpc.catalog.store.currentMenu.useQuery(storeInput)
  const productsQuery = trpc.catalog.store.eligibleProducts.useQuery(storeInput)
  const proposalQuery = trpc.orders.proposalForWeek.useQuery(storeInput)
  // Category names decorate the rows; they neither gate the screen nor fail it,
  // so they stay out of isLoading/isError (a row shows "—" until they land).
  //
  // Deliberately unconditional, even on the screens that never render a table
  // (loading, error, no proposal). Gating it on the order would re-introduce the
  // waterfall — categories would only start loading once the
  // order arrived, so "—" would flash on every visit — and what it would save is
  // one request for a small client-scoped list the query cache shares anyway.
  const categoriesQuery = trpc.catalog.categories.list.useQuery({})

  // A NOT_FOUND from currentMenu is a DOMAIN STATE, not a failure: the week
  // being ordered for has no carta until the operator generates it. That is
  // where every store sits for MOST of the week, not briefly — the week becomes
  // orderable at the store's cutoff (the general Wednesday 13:00, or its own)
  // and its carta only arrives when Operaciones generates it, so the empty state
  // is the norm and the populated one is the last stretch. Matched by code and on this query alone —
  // eligibleProducts answers an empty list in that state rather than a 404, and
  // proposalForWeek answers null, so neither of them can mean this. Any other
  // code, on any of the three, is a real failure and still fails the screen.
  const hasNoCartaYet = menuQuery.isError && getErrorCode(menuQuery.error) === 'NOT_FOUND'

  const isLoading = menuQuery.isLoading || productsQuery.isLoading || proposalQuery.isLoading
  const isError =
    (menuQuery.isError && !hasNoCartaYet) || productsQuery.isError || proposalQuery.isError

  // Capture all inputs as primitives so the deps array is fully explicit —
  // no suppression needed and no silent dep drops when the hook evolves.
  // With no carta, the three answers are collapsed into the one state the page
  // can render: no menu, no products, no order — its no-proposal branch.
  const menuData = hasNoCartaYet ? undefined : menuQuery.data
  const productsData = hasNoCartaYet ? undefined : productsQuery.data
  const categoriesData = categoriesQuery.data
  // proposalForWeek returns null (no proposal yet); undefined means still loading.
  const orderData = hasNoCartaYet ? null : proposalQuery.data

  // The rotation instant is a property of the WEEK, not of the proposal: the
  // no-proposal state spans most of the week, and a screen left open across the
  // cutoff must still move to the week now orderable. A proposal's sealed
  // cutoffAt stays the domain truth whenever one exists.
  const weekCutoff = cutoffForWeek(startOfWetacaWeek(isoYear, isoWeek), cutoff)
  const nowMs = useNowAcrossCutoff(orderData?.cutoffAt ?? weekCutoff)

  return useMemo(
    () =>
      mergeProposalViewModel({
        menu: menuData,
        products: productsData,
        categories: categoriesData,
        order: orderData,
        role,
        isLoading,
        isError,
        week: {isoYear, isoWeek},
        minQuantityPerProduct,
        nowMs,
      }),
    [menuData, productsData, categoriesData, orderData, role, isLoading, isError, isoYear, isoWeek, minQuantityPerProduct, nowMs],
  )
}
